import React, { useCallback, useEffect, useMemo, useState } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { ShieldCheck, Gauge, FileMagnifyingGlass } from "@phosphor-icons/react";

import { t } from "./i18n";
import LanguageSwitcher from "./components/LanguageSwitcher";
import AnalyzerForm from "./components/AnalyzerForm";
import ScoreGauge from "./components/ScoreGauge";
import ViolationsList from "./components/ViolationsList";
import HistorySidebar from "./components/HistorySidebar";
import BatchResults from "./components/BatchResults";
import ExportButtons from "./components/ExportButtons";
import ShopifyPanel from "./components/ShopifyPanel";
import RejectionPanel from "./components/RejectionPanel";
import ComplianceWizard from "./components/ComplianceWizard";
import {
  runAnalyze,
  runBatchAnalyze,
  getHistory,
  getReport,
  deleteReport,
} from "./lib/api";

function App() {
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem("ma_lang");
    if (saved) return saved;
    const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
    return ["fr", "en", "es", "it", "pt", "de"].includes(nav) ? nav : "en";
  });

  const [history, setHistory] = useState([]);
  const [report, setReport] = useState(null);
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [view, setView] = useState("audit"); // audit | shopify

  useEffect(() => {
    localStorage.setItem("ma_lang", lang);
  }, [lang]);

  // Handle Shopify OAuth success redirect (?shopify_oauth=ok&connection_id=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("shopify_oauth") === "ok") {
      setView("shopify");
      toast.success(t(lang, "shopify_connected"));
      // Clean URL
      const clean = window.location.pathname;
      window.history.replaceState({}, "", clean);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const items = await getHistory();
      setHistory(items);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleAudit = async (payload) => {
    setLoading(true);
    setReport(null);
    setBatch(null);
    setActiveId(null);
    try {
      const res = await runAnalyze(payload);
      setReport(res);
      setActiveId(res.id);
      loadHistory();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Error";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleBatch = async (payload) => {
    setLoading(true);
    setReport(null);
    setBatch(null);
    setActiveId(null);
    try {
      const res = await runBatchAnalyze(payload);
      setBatch(res);
      loadHistory();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Error";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectHistory = async (id) => {
    setLoading(true);
    setBatch(null);
    try {
      const res = await getReport(id);
      setReport(res);
      setActiveId(id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHistory = async (id) => {
    try {
      await deleteReport(id);
      toast.success(t(lang, "deleted"));
      if (activeId === id) {
        setReport(null);
        setActiveId(null);
      }
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const headerSubtitle = useMemo(() => t(lang, "tagline"), [lang]);

  return (
    <div className="App min-h-screen bg-[#FAFAFA] text-neutral-900" data-testid="app-root">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <header className="border-b border-neutral-900 bg-white">
        <div className="flex items-center gap-4 px-4 md:px-8 py-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-neutral-900 text-white flex items-center justify-center">
              <ShieldCheck size={18} weight="bold" />
            </div>
            <div className="leading-tight">
              <div className="font-display text-lg font-bold tracking-tight">
                MerchantAudit
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-neutral-500">
                {headerSubtitle}
              </div>
            </div>
          </div>
          <div className="hidden md:flex items-center ml-6 gap-4 text-[10px] font-mono uppercase tracking-widest text-neutral-500">
            <span className="flex items-center gap-1.5">
              <Gauge size={12} /> Compliance
            </span>
            <span className="flex items-center gap-1.5">
              <FileMagnifyingGlass size={12} /> Audit
            </span>
          </div>
          <div className="ml-auto">
            <LanguageSwitcher value={lang} onChange={setLang} />
          </div>
        </div>
        <div className="h-1 grid-skeleton" />
      </header>

      {/* Body */}
      <div className="flex flex-col lg:flex-row min-h-[calc(100vh-81px)]">
        <HistorySidebar
          items={history}
          onSelect={handleSelectHistory}
          onDelete={handleDeleteHistory}
          activeId={activeId}
          lang={lang}
        />

        <main className="flex-1 min-w-0 p-4 md:p-8 space-y-6 ma-scroll">
          {/* View tabs */}
          <div className="flex border border-neutral-900 bg-white w-fit" data-testid="view-tabs">
            <button
              type="button"
              onClick={() => setView("audit")}
              className={`px-5 py-2 text-xs uppercase tracking-widest font-mono ma-trans ${
                view === "audit"
                  ? "bg-neutral-900 text-white"
                  : "bg-white text-neutral-900 hover:bg-neutral-100"
              }`}
              data-testid="view-tab-audit"
            >
              {t(lang, "audit_tab")}
            </button>
            <button
              type="button"
              onClick={() => setView("rejection")}
              className={`px-5 py-2 text-xs uppercase tracking-widest font-mono border-l border-neutral-900 ma-trans ${
                view === "rejection"
                  ? "bg-neutral-900 text-white"
                  : "bg-white text-neutral-900 hover:bg-neutral-100"
              }`}
              data-testid="view-tab-rejection"
            >
              {t(lang, "rejection_tab")}
            </button>
            <button
              type="button"
              onClick={() => setView("shopify")}
              className={`px-5 py-2 text-xs uppercase tracking-widest font-mono border-l border-neutral-900 ma-trans ${
                view === "shopify"
                  ? "bg-neutral-900 text-white"
                  : "bg-white text-neutral-900 hover:bg-neutral-100"
              }`}
              data-testid="view-tab-shopify"
            >
              {t(lang, "shopify_tab")}
            </button>
            <button
              type="button"
              onClick={() => setView("compliance")}
              className={`px-5 py-2 text-xs uppercase tracking-widest font-mono border-l border-neutral-900 ma-trans ${
                view === "compliance"
                  ? "bg-neutral-900 text-white"
                  : "bg-white text-neutral-900 hover:bg-neutral-100"
              }`}
              data-testid="view-tab-compliance"
            >
              {t(lang, "compliance_tab")}
            </button>
          </div>

          {view === "shopify" ? (
            <ShopifyPanel lang={lang} />
          ) : view === "rejection" ? (
            <RejectionPanel lang={lang} />
          ) : view === "compliance" ? (
            <ComplianceWizard lang={lang} />
          ) : (
            <>
          <section className="grid grid-cols-1 xl:grid-cols-5 gap-6">
            <div className="xl:col-span-2">
              <AnalyzerForm
                lang={lang}
                onAudit={handleAudit}
                onBatch={handleBatch}
                loading={loading}
              />
            </div>
            <div className="xl:col-span-3 space-y-6">
              {report && !batch ? (
                <>
                  <ScoreGauge
                    score={report.compliance_score}
                    stats={report.stats || {}}
                    lang={lang}
                  />
                  <div
                    className="border border-neutral-900 bg-white p-5 ma-in"
                    data-testid="summary-card"
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                      <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-neutral-500">
                        {t(lang, "summary")}
                      </div>
                      <ExportButtons lang={lang} kind="report" id={report.id} />
                    </div>
                    <p
                      className="text-sm leading-relaxed text-neutral-800"
                      data-testid="summary-text"
                    >
                      {report.summary || "—"}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 mt-4 pt-4 border-t border-neutral-200 text-xs font-mono">
                      <MetaRow label={t(lang, "source")} value={report.source} testid="meta-source" />
                      <MetaRow
                        label={t(lang, "page_type")}
                        value={t(lang, `page_type_${report.page_type}`)}
                        testid="meta-pagetype"
                      />
                      {report.page_title && (
                        <MetaRow label={t(lang, "page_title")} value={report.page_title} testid="meta-title" />
                      )}
                      {report.meta_description && (
                        <MetaRow label={t(lang, "meta_desc")} value={report.meta_description} testid="meta-description" />
                      )}
                      {report.detected_language && (
                        <MetaRow
                          label={t(lang, "detected_language")}
                          value={`${report.detected_language.toUpperCase()} (${t(lang, "detected_language_signal")}: ${report.detected_language_signal})`}
                          testid="meta-detected-language"
                        />
                      )}
                    </div>
                  </div>
                </>
              ) : !batch ? (
                <EmptyState lang={lang} />
              ) : null}
            </div>
          </section>

          {report && !batch && (
            <section>
              <ViolationsList violations={report.violations} lang={lang} />
            </section>
          )}

          {batch && (
            <section>
              <BatchResults batch={batch} lang={lang} />
            </section>
          )}
            </>
          )}

          <footer className="pt-8 pb-4 text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-400 text-center">
            {t(lang, "footer_note")}
          </footer>
        </main>
      </div>
    </div>
  );
}

function MetaRow({ label, value, testid }) {
  return (
    <div className="flex gap-2 min-w-0" data-testid={testid}>
      <span className="text-neutral-500 uppercase tracking-widest text-[10px] shrink-0">
        {label}
      </span>
      <span className="text-neutral-800 truncate" title={value}>
        {value}
      </span>
    </div>
  );
}

function EmptyState({ lang }) {
  return (
    <div
      className="border border-dashed border-neutral-400 bg-white p-10 text-center grid-skeleton"
      data-testid="empty-state"
    >
      <div className="mx-auto w-12 h-12 border border-neutral-900 flex items-center justify-center mb-4 bg-white">
        <FileMagnifyingGlass size={22} />
      </div>
      <div className="font-display text-xl font-semibold mb-1">
        {t(lang, "audit_report")}
      </div>
      <p className="text-sm text-neutral-500 max-w-md mx-auto">
        {t(lang, "tagline")}
      </p>
    </div>
  );
}

export default App;
